import { computed, toValue, type MaybeRefOrGetter } from 'vue';
import { flattenSavedViewPages, useAllSavedViewsQuery } from '@/composables/api/useSavedViewsApi';
import type { SavedViewRecord } from '@/utils/savedViews';
import { AREAS, entityGate, type AreaId } from '@/navigation/areas';
import { isGateOpen, type Gate } from '@/navigation/gates';

/**
 * The saved views to list in one area's sidebar, oldest first as the list
 * endpoint returns them. That is the user's views for the entities the area
 * holds, and the views of the area's pages that have saved views, such as
 * Track matching in Music.
 *
 * The saved-view endpoint does not check permissions, so a view for an entity
 * the user can no longer view is left out here, and so is a view of a page the
 * user has no grant for. A view that belongs to no area is left out too. None
 * of them is deleted.
 */
export function viewsForArea(views: SavedViewRecord[], areaId: AreaId, gateIsOpen: (gate: Gate) => boolean): SavedViewRecord[] {
    const area = AREAS.find((candidate) => candidate.id === areaId);
    if (!area) {
        return [];
    }
    const entityIds = new Set(area.entities.map((entity) => entity.entityId));
    const pagesByViewEntity = new Map(
        area.pages.filter((page) => page.savedViewEntityId !== undefined).map((page) => [page.savedViewEntityId!, page]),
    );
    return views.filter((view) => {
        if (entityIds.has(view.entity)) {
            return gateIsOpen(entityGate(view.entity));
        }
        const page = pagesByViewEntity.get(view.entity);
        return page !== undefined && (page.gate === null || gateIsOpen(page.gate));
    });
}

/** Every saved view the user owns, loaded once, sorted into the given area. */
export function useAreaSavedViews(areaId: MaybeRefOrGetter<AreaId>) {
    const query = useAllSavedViewsQuery();
    const views = computed(() => viewsForArea(flattenSavedViewPages(query.data.value), toValue(areaId), isGateOpen));
    return {
        views,
        // True until the last page arrives, not only the first. A view on a
        // later page is not here yet, which is not the same as deleted.
        isLoading: computed(() => query.isLoading.value || query.hasNextPage.value),
        isError: computed(() => query.isError.value),
    };
}
